import axiosInstance from '../api/axios';
import { IViewStatusResponse, ViewOrigin } from '../models/contentView';

interface IBody {
  course?: string;
  program?: string;
  /** De onde a aula foi vista. Omitido = `free_browsing` no backend. */
  origin?: ViewOrigin;
  /**
   * Define explicitamente o status de visualização enviado ao backend.
   * - Marcação manual (ícone de check): valor do toggle (próximo estado).
   * - Automático (assistiu a aula de fato): sempre `true`, para não desmarcar
   *   uma aula já concluída ao reassistir.
   */
  viewed?: boolean;
}

interface IProgressBody {
  /** Segundos assistidos. O backend calcula o percentual com a duração da aula. */
  seconds: number;
  course?: string;
  program?: string;
  origin?: ViewOrigin;
}

/**
 * Reporta quanto da aula o aluno já assistiu, para o log de eventos.
 *
 * Só manda os segundos: quem decide se isso é uma aula iniciada ou consumida é
 * o backend, que tem a duração cadastrada e o limiar. Assim a regra é a mesma
 * para o player legado e para o novo, e o front não precisa conhecê-la.
 *
 * Pode ser chamada várias vezes durante a aula — o backend deduplica pelo dia.
 * Não é await de nada na tela: falhar aqui não pode atrapalhar quem assiste.
 */
export async function markLessonProgress(
  slug: string,
  body: IProgressBody
): Promise<void> {
  try {
    await axiosInstance.post(`/lessons/${slug}/progress`, body);
  } catch {
    // Log de progresso é acessório: silenciar é melhor que estourar no player.
  }
}

/**
 * Marca/desmarca a aula como vista. Quando `viewed` é enviado no body, o backend
 * usa esse valor explícito; caso contrário, mantém o comportamento de toggle.
 * Use `viewStatus.viewed` da resposta como fonte da verdade para a UI.
 */
export async function markLessonFinish(
  slug: string,
  body: IBody | null
): Promise<IViewStatusResponse> {
  const response = await axiosInstance.post(`/lessons/${slug}/finished`, body);
  return response.data;
}

export async function getLessonCore(slug: string) {
  try {
    const response = await axiosInstance.get(`/lessons/${slug}`)

    return response.data
  } catch(e: any) {
    throw new Error(e);
  }
}

export type NotesPdfStatus = 'processing' | 'ready' | 'failed';

export interface INotesPdfResponse {
  status: NotesPdfStatus;
  // Presente quando `status === 'ready'`: URL do PDF já gerado.
  url?: string | null;
}

/*
 * Dispara a geração assíncrona do PDF de anotações de uma videoaula.
 * `id` = _id da lesson. O endpoint responde imediatamente (não segura a
 * conexão enquanto gera): `202 { status: 'processing' }` ao enfileirar, ou
 * `200 { status: 'ready', url }` quando o PDF já existe.
 */
export async function startLessonNotesPdf(
  id: string,
  signal?: AbortSignal
): Promise<INotesPdfResponse> {
  const response = await axiosInstance.post(`/lessons/${id}/notes-pdf`, null, {
    signal,
  });
  return response.data;
}

/*
 * Consulta o status da geração do PDF de anotações (polling).
 * Enquanto gera: `{ status: 'processing' }`; ao concluir: `{ status: 'ready',
 * url }`; em falha: `{ status: 'failed' }`.
 */
export async function getLessonNotesPdfStatus(
  id: string,
  signal?: AbortSignal
): Promise<INotesPdfResponse> {
  const response = await axiosInstance.get(`/lessons/${id}/notes-pdf/status`, {
    signal,
  });
  return response.data;
}